import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Activity, Flame, AlertTriangle, Gauge } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area, AreaChart
} from 'recharts';

function ChartCard({ title, icon, children, badge }) {
  return (
    <Card className="bg-[#111111] border-[#27272A] rounded-sm">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-white font-['Outfit'] text-sm">
          {icon}
          {title}
          {badge && <Badge className="bg-zinc-800 text-zinc-400 text-[9px] px-1.5 py-0 rounded-sm ml-auto">{badge}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm px-3 py-2 text-xs">
      <p className="text-zinc-500 mb-1">Row {label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-['JetBrains_Mono']">
          {p.name}: {typeof p.value === 'number' ? p.value.toFixed(2) : p.value}
        </p>
      ))}
    </div>
  );
};

export default function DatalogCharts({ datalog }) {
  const chartData = useMemo(() => {
    if (!datalog?.raw_data) return null;
    const rows = datalog.raw_data;
    return rows.map((row, i) => ({
      idx: i,
      rpm: parseFloat(row['RPM [RPM]'] || 0),
      afr: parseFloat(row['AFR (B1) [AFR]'] || 0),
      afr_tgt: parseFloat(row['AFR TARGET (B1) [AFR]'] || 0),
      timing: parseFloat(row['TIMING- CYL 1 [°CRK]'] || 0),
      load: parseFloat(row['LOAD [%]'] || 0),
      pedal: parseFloat(row['PEDAL INPUT [%]'] || 0),
      torque: parseFloat(row['TORQUE [FT/LBS]'] || 0),
      knock1: parseFloat(row['KNOCK- CYL 1 [V]'] || 0),
      ltft: parseFloat(row['LTFT (B1) [%]'] || 0),
      speed: parseFloat(row['VEHICLE SPEED [MLS]'] || 0),
    }));
  }, [datalog]);

  // Fallback: build from analysis data if raw_data not available
  // Supports both rpm_bands array and wot object with RPM range keys
  const bandData = useMemo(() => {
    // Try rpm_bands array first
    if (datalog?.analysis?.rpm_bands) {
      return datalog.analysis.rpm_bands.map(b => ({
        range: b.range,
        afr: b.avg_afr,
        timing: b.avg_timing,
        torque: b.max_torque || b.peak_torque || 0,
        status: b.status,
      }));
    }
    // Fallback to wot object with RPM range keys (e.g., "3000-4000")
    if (datalog?.analysis?.wot && Object.keys(datalog.analysis.wot).length > 0) {
      return Object.entries(datalog.analysis.wot)
        .sort(([a], [b]) => parseInt(a) - parseInt(b))
        .map(([range, data]) => ({
          range,
          afr: data.afr || 0,
          timing: data.timing || 0,
          torque: data.torque || 0,
          status: data.afr_status || 'UNKNOWN',
        }));
    }
    return null;
  }, [datalog]);

  if (!chartData && !bandData) {
    return (
      <Card className="bg-[#111111] border-[#27272A] rounded-sm">
        <CardContent className="py-10 text-center">
          <Activity className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
          <p className="text-sm text-zinc-500">No chart data available for this datalog</p>
        </CardContent>
      </Card>
    );
  }

  // Use RPM band data if no raw data
  if (!chartData && bandData) {
    return (
      <div className="space-y-4" data-testid="datalog-charts">
        <ChartCard title="AFR by RPM Band" icon={<Flame className="w-4 h-4 text-[#FF5E1A]" />} badge={`${bandData.length} bands`}>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={bandData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis dataKey="range" tick={{ fill: '#71717A', fontSize: 10 }} />
              <YAxis tick={{ fill: '#71717A', fontSize: 10 }} domain={[10, 20]} />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={12.0} stroke="#22C55E" strokeDasharray="5 5" label={{ value: "E85 Target", fill: "#22C55E", fontSize: 9 }} />
              <ReferenceLine y={14.7} stroke="#EF4444" strokeDasharray="5 5" label={{ value: "Gasoline", fill: "#EF4444", fontSize: 9 }} />
              <Area type="monotone" dataKey="afr" stroke="#FF5E1A" fill="#FF5E1A" fillOpacity={0.1} strokeWidth={2} name="AFR" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    );
  }

  // Full time-series charts from raw data
  const validAfr = chartData.filter(d => d.afr > 0 && d.afr < 30);
  const hasKnock = chartData.some(d => d.knock1 > 2.0);

  return (
    <div className="space-y-4" data-testid="datalog-charts">
      {/* RPM vs AFR */}
      <ChartCard title="AFR vs RPM (Time Series)" icon={<Flame className="w-4 h-4 text-[#FF5E1A]" />} badge={`${validAfr.length} pts`}>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={validAfr} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
            <XAxis dataKey="idx" tick={{ fill: '#71717A', fontSize: 9 }} />
            <YAxis yAxisId="afr" tick={{ fill: '#71717A', fontSize: 9 }} domain={[10, 20]} />
            <YAxis yAxisId="rpm" orientation="right" tick={{ fill: '#71717A', fontSize: 9 }} domain={[0, 7200]} />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine yAxisId="afr" y={12.0} stroke="#22C55E" strokeDasharray="5 5" />
            <ReferenceLine yAxisId="afr" y={14.7} stroke="#EF4444" strokeDasharray="5 5" />
            <Line yAxisId="afr" type="monotone" dataKey="afr" stroke="#FF5E1A" strokeWidth={1.5} dot={false} name="AFR" />
            <Line yAxisId="afr" type="monotone" dataKey="afr_tgt" stroke="#666" strokeWidth={1} dot={false} name="AFR Target" strokeDasharray="4 4" />
            <Line yAxisId="rpm" type="monotone" dataKey="rpm" stroke="#22D3EE" strokeWidth={1} dot={false} name="RPM" opacity={0.5} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Timing & Load */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChartCard title="Timing Advance" icon={<Gauge className="w-4 h-4 text-cyan-400" />}>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis dataKey="idx" tick={{ fill: '#71717A', fontSize: 9 }} />
              <YAxis tick={{ fill: '#71717A', fontSize: 9 }} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="timing" stroke="#22D3EE" fill="#22D3EE" fillOpacity={0.1} strokeWidth={1.5} name="Timing °" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={hasKnock ? "Knock Events DETECTED" : "Knock (Clean)"} icon={<AlertTriangle className={`w-4 h-4 ${hasKnock ? 'text-red-400' : 'text-emerald-400'}`} />}>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis dataKey="idx" tick={{ fill: '#71717A', fontSize: 9 }} />
              <YAxis tick={{ fill: '#71717A', fontSize: 9 }} domain={[0, 5]} />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={2.0} stroke="#EF4444" strokeDasharray="5 5" label={{ value: "Knock Threshold", fill: "#EF4444", fontSize: 9 }} />
              <Line type="monotone" dataKey="knock1" stroke={hasKnock ? "#EF4444" : "#22C55E"} strokeWidth={1.5} dot={false} name="Knock V" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}
